import Cookies from 'universal-cookie';
import { addDays } from 'date-fns';
import { logout } from '@services/auth.service';
import { isZoomApp } from '@zoom-app/helpers';

export const isRunningInIframe = () => {
  try {
    return window.location.href !== window.top.location.href;
  } catch (e) {
    return true;
  }
};

const cookies = new Cookies();

export const setUniversalLoggedInCookie = () => cookies.set('IS_LOGGED_IN', 'true', { path: '/', domain: process.env.NEXT_PUBLIC_BASE_DOMAIN });

export const removeUniversalLoggedInCookie = () => cookies.remove('IS_LOGGED_IN', { path: '/', domain: process.env.NEXT_PUBLIC_BASE_DOMAIN });

export const getUniversalLoggedInCookie = () => cookies.get('IS_LOGGED_IN');

export const TEAM_COOKIE_NAME = 'SA_TEAM';

const EMBED_AUTH_STORAGE_KEY = 'SA_EMBED_AUTH';

const writeEmbedAuthToStorage = (authObject) => {
  if (typeof window === 'undefined' || !isRunningInIframe()) return;
  try {
    window.localStorage.setItem(EMBED_AUTH_STORAGE_KEY, JSON.stringify(authObject));
  } catch (e) {
    // Storage unavailable (e.g. fully blocked) — cookie path is the only hope.
  }
};

export const removeEmbedAuthFromStorage = () => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(EMBED_AUTH_STORAGE_KEY);
  } catch (e) {
    // ignore
  }
};

export const getAuth = () => cookies.get('AUTH');

export const getAuthExpiryDt = () => cookies.get('AUTH_EXPIRY_DT');

export const getExpiryDate = () => addDays(new Date(), 14);

export const getCookieOptions = (expiryValue, domain) => {
  const options = {
    path: '/',
    secure: true,
    sameSite: (isRunningInIframe() || isZoomApp()) ? 'none' : 'lax'
  };
  if (expiryValue) {
    options.expires = expiryValue;
  }
  if (domain) {
    options.domain = domain;
  }
  return options;
};

export const setAuth = (authObject, domain, expiryValue = getExpiryDate()) => {
  const options = getCookieOptions(expiryValue, domain);

  cookies.set('AUTH', JSON.stringify(authObject), options);
  cookies.set('AUTH_EXPIRY_DT', expiryValue, options);
  writeEmbedAuthToStorage(authObject);
  return authObject;
};

export const removeAuth = async () => {
  try {
    const res = await logout();
    console.log('Logout response: ', res);
  } catch (error) {
    console.error('Error logging out:', error);
  }
  // cookies.remove('AUTH', { path: '/' });
  // cookies.remove('AUTH_EXPIRY_DT', { path: '/' });

  removeAuthFromDomain();
  removeUniversalLoggedInCookie();
};

export const removeAuthFromDomain = () => {
  cookies.remove('AUTH', { path: '/', domain: process.env.NEXT_PUBLIC_BASE_DOMAIN });
  cookies.remove('AUTH_EXPIRY_DT', { path: '/', domain: process.env.NEXT_PUBLIC_BASE_DOMAIN });
  removeEmbedAuthFromStorage();
};

export const isInRole = (role, user) => user.roles && user.roles.includes(role);

export const isAuthenticated = (user) => user != null && user.token && !['copilot', 'guest'].includes(user.userRole);

// A real signed-in user allowed to open authenticated background connections
// (e.g. the realtime-sync RTDB sign-in). Unlike isAuthenticated, copilots pass
// through — they act on behalf of a real team — but guests and the
// unauthenticated (no token) are excluded, so we never initiate the RTDB token
// fetch from the login page / a guest session.
export const isRealUser = (user) => user != null && !!user.token && user.userRole !== 'guest';

export const isAdmin = () => {
  const auth = cookies.get('AUTH');

  if (!auth) return false;

  return auth.userRole === 'admin';
};

export const getCurrentTeamToken = () => cookies.get(TEAM_COOKIE_NAME);

export const setCurrentTeamToken = (token) => {
  const options = getCookieOptions();
  cookies.set(TEAM_COOKIE_NAME, token, options);
};

export const removeCurrentTeamToken = () => {
  cookies.remove(TEAM_COOKIE_NAME, { path: '/' });
  cookies.remove(TEAM_COOKIE_NAME, { path: '/', domain: process.env.NEXT_PUBLIC_BASE_DOMAIN });
};

export const setBanner = (domain, expiryValue = addDays(new Date(), 14)) => {
  const options = { path: '/', expires: expiryValue };
  if (domain) {
    options.domain = domain;
  }

  cookies.set('VIMEO_SHOW_BANNER', 'true', options);
  cookies.set('YOUTUBE_SHOW_BANNER', 'true', options);
  cookies.set('WEBEX_SHOW_BANNER', 'true', options);
  cookies.set('MEET_SHOW_BANNER', 'true', options);
  cookies.set('LINKEDIN_SHOW_BANNER', 'true', options);
};

export const getBanner = (cookieName) => cookies.get(cookieName);

export const removeBanner = (cookieName) => {
  cookies.remove(cookieName, { path: '/' });
};
